import {
  AccountStatus,
  ApiKeyStatus,
  ChannelPaymentStatus,
  ChannelPaymentSubStatus,
  ChannelSettlementStatus,
  ChannelStatus,
  InvoicePolicyAction,
  InvoiceStatus,
  InvoiceSubStatus,
  MerchantStatus,
  RefundStatus,
  TransactionStatus,
  TransientWalletStatus,
  UnattachedDepositStatus,
  WebhookDeliveryStatus,
  WebhookEvent,
  WithdrawalStatus,
} from "@cpg/common";

// ─── Semantic status-color palette ──────────────────────────────────────────
//
// One small, fixed vocabulary of dot colors, reused by every domain enum
// below rather than inventing a new hex per enum value. `status-badge.tsx`
// renders whichever `StatusColor` a call site resolves via the maps here —
// this file is the only place that knows how an enum value maps to meaning.
//
// InvoiceStatus/MerchantStatus preserve the exact colors the pre-existing
// per-page pills (_app.invoices.tsx, _app.merchants.tsx, _app.high-risk.tsx)
// used, folded onto this palette:
//  - InvoiceStatus.NEW was `#6B7280` (a second, darker gray with no palette
//    slot of its own) — folded into `neutral` (`#9CA3AF`); both are muted
//    grays for an inactive/unremarkable state and the visual difference at
//    dot size is not meaningful.
//  - InvoiceStatus.REJECTED was `#DC2626` (a darker red than `danger`'s
//    `#EF4444`) — folded into `danger` per the task brief's suggestion; the
//    rejected-vs-action-required distinction wasn't carrying meaning.
//
// The other 7 enums have no pre-existing frontend precedent; each value is
// assigned a StatusColor by this convention: a terminal successful/available
// state -> success; an in-progress/awaiting state -> warning; an
// actively-processing state -> info; a failed/blocked/needs-attention state
// -> danger; a terminal-but-unremarkable state (expired, cancelled, swept,
// revoked) -> neutral. See task-20-report.md for the full per-enum rationale.

export type StatusColor = "success" | "warning" | "danger" | "info" | "neutral" | "purple";

export const STATUS_DOT_COLOR: Record<StatusColor, string> = {
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  info: "#3B82F6",
  neutral: "#9CA3AF",
  purple: "#8B5CF6",
};

export const INVOICE_STATUS_COLOR: Record<InvoiceStatus, StatusColor> = {
  [InvoiceStatus.NEW]: "neutral",
  [InvoiceStatus.CONFIRMING]: "warning",
  [InvoiceStatus.PARTIALLY_PAID]: "warning",
  // feature-034 — same "in flight, nothing for an admin to do" family as
  // CONFIRMING; the deposit is confirmed, the sweep hasn't started.
  [InvoiceStatus.CONFIRMED]: "warning",
  [InvoiceStatus.SETTLING]: "info",
  [InvoiceStatus.COMPLETED]: "success",
  [InvoiceStatus.ACTION_REQUIRED]: "danger",
  [InvoiceStatus.REFUNDED]: "purple",
  [InvoiceStatus.EXPIRED]: "neutral",
  [InvoiceStatus.REJECTED]: "danger",
};

export const MERCHANT_STATUS_COLOR: Record<MerchantStatus, StatusColor> = {
  [MerchantStatus.ACTIVE]: "success",
  [MerchantStatus.SUSPENDED]: "danger",
  [MerchantStatus.PENDING_KYB]: "warning",
};

// InvoiceSubStatus is only ever set alongside InvoiceStatus.ACTION_REQUIRED
// (never rendered with its own dot today — the existing pill convention
// shows it as a `subLabel` suffix on the ACTION_REQUIRED dot). Every value
// is itself a flavor of "needs administrative attention", so all map to
// `danger`, consistent with ACTION_REQUIRED's own color.
export const INVOICE_SUB_STATUS_COLOR: Record<InvoiceSubStatus, StatusColor> = {
  [InvoiceSubStatus.UNDERPAID]: "danger",
  [InvoiceSubStatus.OVERPAID]: "danger",
  [InvoiceSubStatus.PAID_LATE]: "danger",
  [InvoiceSubStatus.HIGH_RISK]: "danger",
  [InvoiceSubStatus.SETTLEMENT_FAILED]: "danger",
};

export const ACCOUNT_STATUS_COLOR: Record<AccountStatus, StatusColor> = {
  [AccountStatus.PENDING]: "warning",
  [AccountStatus.ACTIVE]: "success",
  [AccountStatus.FAILED]: "danger",
};

// SWEPT is a strictly-forward terminal state (wallets never return to
// AVAILABLE) — an unremarkable "done with its job" state, not a failure.
export const TRANSIENT_WALLET_STATUS_COLOR: Record<TransientWalletStatus, StatusColor> = {
  [TransientWalletStatus.AVAILABLE]: "success",
  [TransientWalletStatus.OCCUPIED]: "info",
  [TransientWalletStatus.SWEPT]: "neutral",
};

// CONFIRMING here is an actively-happening blockchain process (accumulating
// confirmations), the same "in-flight pipeline" role InvoiceStatus.SETTLING
// plays -> `info`, not `warning` (contrast InvoiceStatus.CONFIRMING, which
// preserves its pre-existing `warning` color and is a different enum).
export const TRANSACTION_STATUS_COLOR: Record<TransactionStatus, StatusColor> = {
  [TransactionStatus.PENDING]: "warning",
  [TransactionStatus.CONFIRMING]: "info",
  [TransactionStatus.COMPLETED]: "success",
  [TransactionStatus.FROZEN]: "danger",
  [TransactionStatus.FAILED]: "danger",
};

export const REFUND_STATUS_COLOR: Record<RefundStatus, StatusColor> = {
  [RefundStatus.OPEN]: "warning",
  [RefundStatus.CLAIMED]: "info",
  [RefundStatus.COMPLETED]: "success",
  [RefundStatus.FAILED]: "danger",
  [RefundStatus.CANCELLED]: "neutral",
};

export const WEBHOOK_DELIVERY_STATUS_COLOR: Record<WebhookDeliveryStatus, StatusColor> = {
  [WebhookDeliveryStatus.PENDING]: "warning",
  [WebhookDeliveryStatus.SUCCEEDED]: "success",
  [WebhookDeliveryStatus.EXHAUSTED]: "danger",
};

// Each WebhookEvent names an InvoiceStatus (or InvoiceSubStatus) transition
// (`invoice.confirming`, `invoice.completed`, ...) — colors mirror
// INVOICE_STATUS_COLOR's for the same transition, so an event badge reads
// the same as the status dot it announces. WEBHOOK_TEST isn't a real
// invoice event (a merchant's own connectivity check) -> `neutral`.
export const WEBHOOK_EVENT_COLOR: Record<WebhookEvent, StatusColor> = {
  [WebhookEvent.INVOICE_CONFIRMING]: "warning",
  [WebhookEvent.INVOICE_CONFIRMED]: "warning",
  [WebhookEvent.INVOICE_PARTIALLY_PAID]: "warning",
  [WebhookEvent.INVOICE_SETTLING]: "info",
  [WebhookEvent.INVOICE_COMPLETED]: "success",
  [WebhookEvent.INVOICE_EXPIRED]: "neutral",
  [WebhookEvent.INVOICE_ACTION_REQUIRED]: "danger",
  [WebhookEvent.INVOICE_REJECTED]: "danger",
  [WebhookEvent.INVOICE_REFUNDED]: "purple",
  [WebhookEvent.WEBHOOK_TEST]: "neutral",
  // Payment channels (feature-031 Task 14) — mirrors the invoice mapping
  // above for the same status transition (CONFIRMING/SETTLING -> warning/
  // info's own precedent doesn't apply here since channel payments have no
  // "partially paid" analogue; SETTLING is its own in-progress transition,
  // colored `info` to match CHANNEL_PAYMENT_STATUS_COLOR.SETTLING above).
  [WebhookEvent.CHANNEL_PAYMENT_CONFIRMING]: "warning",
  [WebhookEvent.CHANNEL_PAYMENT_CONFIRMED]: "info",
  [WebhookEvent.CHANNEL_PAYMENT_SETTLING]: "info",
  [WebhookEvent.CHANNEL_PAYMENT_COMPLETED]: "success",
  [WebhookEvent.CHANNEL_PAYMENT_ACTION_REQUIRED]: "danger",
  [WebhookEvent.CHANNEL_PAYMENT_REFUNDED]: "purple",
  [WebhookEvent.CHANNEL_PAYMENT_CANCELLED]: "neutral",
  [WebhookEvent.CHANNEL_PAYMENT_FAILED]: "danger",
  // feature-037 — mirrors UNATTACHED_DEPOSIT_STATUS_COLOR below.
  [WebhookEvent.UNATTACHED_DEPOSIT_DETECTED]: "warning",
  [WebhookEvent.UNATTACHED_DEPOSIT_CONFIRMED]: "warning",
  [WebhookEvent.UNATTACHED_DEPOSIT_ACTION_REQUIRED]: "danger",
  [WebhookEvent.UNATTACHED_DEPOSIT_COMPLETED]: "success",
  [WebhookEvent.UNATTACHED_DEPOSIT_REFUNDED]: "purple",
  [WebhookEvent.UNATTACHED_DEPOSIT_REJECTED]: "danger",
};

// feature-037: a deposit to a closed invoice. AWAITING_DECISION waits on the
// merchant -> warning; CREDITING/REFUND_PENDING are in flight -> info.
export const UNATTACHED_DEPOSIT_STATUS_COLOR: Record<UnattachedDepositStatus, StatusColor> = {
  [UnattachedDepositStatus.CONFIRMING]: "warning",
  [UnattachedDepositStatus.AWAITING_DECISION]: "warning",
  [UnattachedDepositStatus.CREDITING]: "info",
  [UnattachedDepositStatus.COMPLETED]: "success",
  [UnattachedDepositStatus.REFUND_PENDING]: "info",
  [UnattachedDepositStatus.REFUNDED]: "purple",
  [UnattachedDepositStatus.ACTION_REQUIRED]: "danger",
  [UnattachedDepositStatus.REJECTED]: "danger",
  [UnattachedDepositStatus.FAILED]: "neutral",
};

// ApiKeyStatus.REVOKED is the key's own terminal lifecycle state (an
// administrator deliberately deactivated it) -> `neutral`, same bucket as
// "expired"/"cancelled"/"swept". Distinct from the merchant-access boolean
// pill's "revoked" (danger, preserved below) -- that one describes an
// active merchant losing portal access right now, a materially different,
// more alarming event than an old API key being retired.
export const API_KEY_STATUS_COLOR: Record<ApiKeyStatus, StatusColor> = {
  [ApiKeyStatus.ACTIVE]: "success",
  [ApiKeyStatus.REVOKED]: "neutral",
};

// MANUAL leaves the sub-status for an admin to resolve by hand (via the
// normal high-risk/action-required queue) -> `warning` (needs attention,
// but not itself alarming). The two auto-resolutions read as outcomes, not
// warnings: AUTO_REFUND actively returns funds -> `info`; AUTO_COMPLETE
// accepts the invoice as paid -> `success`.
export const INVOICE_POLICY_ACTION_COLOR: Record<InvoicePolicyAction, StatusColor> = {
  [InvoicePolicyAction.MANUAL]: "warning",
  [InvoicePolicyAction.AUTO_REFUND]: "info",
  [InvoicePolicyAction.AUTO_COMPLETE]: "success",
};

// Feature 030: PENDING_REVIEW awaits an admin decision -> `warning`;
// APPROVED/SENDING are both in-flight toward payout -> `info`; COMPLETED is
// the terminal success -> `success`; REJECTED/FAILED are both terminal
// failures -> `danger`.
export const WITHDRAWAL_STATUS_COLOR: Record<WithdrawalStatus, StatusColor> = {
  [WithdrawalStatus.PENDING_REVIEW]: "warning",
  [WithdrawalStatus.APPROVED]: "info",
  [WithdrawalStatus.SENDING]: "info",
  [WithdrawalStatus.COMPLETED]: "success",
  [WithdrawalStatus.REJECTED]: "danger",
  [WithdrawalStatus.FAILED]: "danger",
};

// Task 14 (feature-031 Phase B) — real internal channel/payment/settlement
// vocabulary, same per-enum convention as the block above: terminal success
// -> success, in-progress/awaiting -> warning, actively-processing ->
// info, needs-attention/failed -> danger, unremarkable-terminal -> neutral.
export const CHANNEL_STATUS_COLOR: Record<ChannelStatus, StatusColor> = {
  [ChannelStatus.PENDING]: "warning",
  [ChannelStatus.ACTIVE]: "success",
  [ChannelStatus.FAILED]: "danger",
  [ChannelStatus.ARCHIVED]: "neutral",
};

export const CHANNEL_PAYMENT_STATUS_COLOR: Record<ChannelPaymentStatus, StatusColor> = {
  [ChannelPaymentStatus.CONFIRMING]: "warning",
  [ChannelPaymentStatus.CONFIRMED]: "info",
  [ChannelPaymentStatus.SETTLING]: "info",
  [ChannelPaymentStatus.COMPLETED]: "success",
  [ChannelPaymentStatus.ACTION_REQUIRED]: "danger",
  [ChannelPaymentStatus.REFUNDED]: "purple",
  [ChannelPaymentStatus.CANCELLED]: "neutral",
  [ChannelPaymentStatus.FAILED]: "danger",
};

// Only ever set alongside ChannelPaymentStatus.ACTION_REQUIRED — same
// all-danger convention as INVOICE_SUB_STATUS_COLOR above.
export const CHANNEL_PAYMENT_SUB_STATUS_COLOR: Record<ChannelPaymentSubStatus, StatusColor> = {
  [ChannelPaymentSubStatus.HIGH_RISK]: "danger",
  [ChannelPaymentSubStatus.SETTLEMENT_FAILED]: "danger",
};

export const CHANNEL_SETTLEMENT_STATUS_COLOR: Record<ChannelSettlementStatus, StatusColor> = {
  [ChannelSettlementStatus.PENDING]: "warning",
  [ChannelSettlementStatus.SENDING]: "info",
  [ChannelSettlementStatus.COMPLETED]: "success",
  [ChannelSettlementStatus.FAILED]: "danger",
};

// The two ad-hoc boolean pills (merchant access, deposit freeze) aren't
// enum-backed, so their colors are resolved inline at each call site
// (`color={granted ? "success" : "danger"}`) rather than through a map here
// — colors preserved exactly from the pre-existing AccessPill/FrozenBadge
// components.
